import { ArrowDown, ArrowRight } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { HeroDesk } from "./HeroDesk";

// Served at a stable URL by the /assets/:file route (not bundler imports) so the
// src is identical in the client bundle and server-rendered HTML.
const heroWebp = "/assets/therapist-hero.webp";
const heroJpg = "/assets/therapist-hero.jpg";

export function Hero() {
  const ref = useScrollReveal<HTMLElement>();
  const { t } = useTranslation();

  return (
    <section
      ref={ref}
      className="animate-on-scroll relative flex min-h-[100dvh] flex-col justify-center overflow-hidden px-5 pb-28 pt-24 sm:px-6 sm:pb-32 lg:px-8"
      style={{ background: "var(--color-ther-ground)" }}
    >
      {/* Background photo. Focal point pushed right for the same reason as on
          the home hero: a phone crops most of the width away. */}
      <picture>
        <source srcSet={heroWebp} type="image/webp" />
        <img
          src={heroJpg}
          alt=""
          aria-hidden
          className="absolute inset-0 z-0 size-full object-cover"
          style={{ objectPosition: "70% center" }}
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
      </picture>

      {/* Umber tint over the photo, same two-pass shape as the home hero: an
          ellipse opens a clearing over the subject and a linear sweep leans
          the whole thing left, where the headline and the deck need an
          opaque ground to read against. */}
      <div
        aria-hidden
        className="absolute inset-0 z-0"
        style={{
          background: [
            "radial-gradient(115% 95% at 74% 38%, transparent 0%, color-mix(in oklab, var(--color-ther-ground) 26%, transparent) 52%, color-mix(in oklab, var(--color-ther-ground-deep) 66%, transparent) 100%)",
            "linear-gradient(103deg, var(--color-ther-ground) 0%, var(--color-ther-ground) 20%, color-mix(in oklab, var(--color-ther-ground) 90%, transparent) 44%, color-mix(in oklab, var(--color-ther-ground) 62%, transparent) 72%, color-mix(in oklab, var(--color-ther-ground) 42%, transparent) 100%)",
          ].join(", "),
        }}
      />
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 z-0 h-40"
        style={{
          background:
            "linear-gradient(to bottom, color-mix(in oklab, var(--color-ther-ground-deep) 78%, transparent) 0%, transparent 100%)",
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl">
        {/* Two voices in one headline: the promise in the heaviest weight the
            face has, the relief in a light italic. The contrast of weight is
            the treatment; the home hero sets its whole line in one weight. */}
        <h1
          className="font-heading animate-hero-rise max-w-[18ch] leading-[1.02] tracking-[-0.035em]"
          style={{
            fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
            color: "var(--color-paper)",
            animationDelay: "60ms",
          }}
        >
          <span className="block font-extrabold">{t("forTherapists.hero.title")}</span>
          <span
            className="block font-light italic tracking-[-0.02em]"
            style={{ color: "var(--color-ther-sage)" }}
          >
            {t("forTherapists.hero.titleHighlight")}.
          </span>
        </h1>

        {/* Subheadline hangs off a clay rule instead of floating free. */}
        <p
          className="animate-hero-rise mt-7 max-w-[40ch] border-l-2 pl-5 text-base leading-relaxed sm:text-lg"
          style={{
            color: "color-mix(in oklab, var(--color-paper) 80%, transparent)",
            borderColor: "var(--color-ther-clay-light)",
            animationDelay: "180ms",
          }}
        >
          {t("forTherapists.hero.subtitle")}
        </p>

        <div
          className="animate-hero-rise mt-8 flex flex-wrap items-center gap-x-7 gap-y-4"
          style={{ animationDelay: "280ms" }}
        >
          <Link
            to="/therapist-enroll"
            className="ther-cta group inline-flex h-14 items-center justify-center gap-3 rounded-full pl-8 pr-6 text-base font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            {t("forTherapists.hero.ctaPrimary")}
            <ArrowRight
              className="size-4 transition-transform duration-300 group-hover:translate-x-0.5"
              weight="bold"
              aria-hidden
            />
          </Link>
          <a
            href="#process"
            className="inline-flex items-center gap-2 text-sm font-semibold underline decoration-1 underline-offset-[6px] transition-colors hover:decoration-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            style={{
              color: "color-mix(in oklab, var(--color-paper) 85%, transparent)",
              textDecorationColor: "color-mix(in oklab, var(--color-paper) 40%, transparent)",
            }}
          >
            {t("forTherapists.hero.ctaSecondary")}
            <ArrowDown className="size-3.5" weight="bold" aria-hidden />
          </a>
        </div>

        <HeroDesk />
      </div>

      {/* Wavy bottom edge bridging into the linen section below */}
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-[-1px] z-20 h-[70px] w-full sm:h-[110px]"
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0,72 C240,16 480,16 720,52 C960,88 1200,104 1440,56 L1440,120 L0,120 Z"
          fill="var(--color-ther-linen)"
        />
      </svg>
    </section>
  );
}
